package com.oscarherreros.appcalculo.web

import com.oscarherreros.appcalculo.game.GameState
import com.oscarherreros.appcalculo.game.Hint
import com.oscarherreros.appcalculo.game.MAX_STARS
import com.oscarherreros.appcalculo.game.Op
import com.oscarherreros.appcalculo.game.Step
import com.oscarherreros.appcalculo.game.dailyLevel
import com.oscarherreros.appcalculo.game.dailyNumber
import com.oscarherreros.appcalculo.game.encode
import com.oscarherreros.appcalculo.game.restoreGame
import com.oscarherreros.appcalculo.game.worthSaving
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.dom.events.KeyboardEvent
import org.w3c.dom.url.URL
import kotlin.js.Promise
import kotlin.js.json
import kotlin.math.abs

/** La app en Google Play. Mientras no esté publicada, la web solo la anuncia, sin enlace. */
private const val GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.oscarherreros.kalkulia"
private const val APP_PUBLISHED = false

/**
 * El enlace que se comparte, relativo a la página. Es la misma página con otro
 * nombre (la copia `web/build.gradle.kts`): así la analítica, que no guarda los
 * parámetros de la dirección, cuenta aparte las visitas que llegan por un
 * enlace compartido.
 */
private const val SHARED_PAGE = "reto"

/** Lo que se ve «Copiado» en el botón de compartir. */
private const val COPIED_MILLIS = 2000

/** Lo más pequeña que puede quedar una ficha al ajustarla a la pantalla: por debajo cuesta tocarla. */
private const val MIN_TILE_PX = 48.0

/** Margen que se deja entre la partida y el borde inferior de la pantalla. */
private const val FIT_MARGIN_PX = 16.0

private val Step.operation: String get() = "$a ${op.symbol} $b"
private val Step.text: String get() = "$operation = $result"

/** Una ficha del tablero: el botón, su número y lo que daría operarla con la seleccionada. */
private class TileView(val button: HTMLButtonElement) {
    val value = button.span("value")
    val preview = button.span("preview")
}

/**
 * La página del reto diario. Construye la interfaz dentro de [root] una sola vez
 * y después solo la actualiza, para no perder el foco del teclado al jugar.
 */
class DailyPage(root: Element, private val texts: Texts, private val store: DailyStore) {
    private var day = today()
    private var progress = store.loadProgress()
    private var state = GameState(dailyLevel(day))
    private var tiles = emptyList<TileView>()

    private val challengeName: HTMLElement
    private val challengeDate: HTMLElement
    private val statusLine: HTMLElement
    private val targetValue: HTMLElement
    private val expression: HTMLElement
    private val guidance: HTMLElement
    private val tileBox: HTMLElement
    private val opButtons: Map<Op, HTMLButtonElement>
    private val controls: HTMLElement
    private val undoButton: HTMLButtonElement
    private val resetButton: HTMLButtonElement
    private val hintButton: HTMLButtonElement
    private val overlay: HTMLElement

    /** Lo que queda detrás de la tarjeta de reto completado y deja de responder mientras se ve. */
    private val behindOverlay: List<HTMLElement>
    private val solvedCard: HTMLElement
    private val solvedTitle: HTMLElement
    private val solvedStars: HTMLElement
    private val solvedSteps: HTMLElement
    private val solvedTip: HTMLElement
    private val solvedStreak: HTMLElement
    private val shareButton: HTMLButtonElement
    private val stepsBox: HTMLElement
    private val stepsList: HTMLElement
    private val viewportProbe: HTMLElement

    /** El tamaño de ficha que se ha calculado para la pantalla actual, en píxeles. */
    private var tileFit = 0.0
    private var fitPending = false

    init {
        val header = root.child<HTMLElement>("header", "header")
        val brand = header.div("brand")
        brand.child<HTMLElement>("img", "logo").apply {
            setAttribute("src", "icon.svg")
            setAttribute("alt", "")
        }
        brand.child<HTMLElement>("h1", text = "Kalkulia")
        header.paragraph("tagline", texts.tagline)
        // El reto y su fecha. En una pantalla estrecha, la línea se parte entre los dos y no a
        // mitad de la fecha.
        val dateLine = header.paragraph("date")
        challengeName = dateLine.span()
        dateLine.append(" · ")
        challengeDate = dateLine.span()
        statusLine = header.paragraph("status")

        val game = root.child<HTMLElement>("main", "game")
        val target = game.child<HTMLElement>("section", "target")
        target.setAttribute("aria-label", texts.target)
        target.span("label", texts.target)
        targetValue = target.span("number")

        val expressionBar = game.div("expression")
        expression = expressionBar.paragraph("operation")
        guidance = expressionBar.paragraph("guidance")
        guidance.setAttribute("aria-live", "polite")

        tileBox = game.div("tiles")

        controls = game.div("controls")
        val ops = controls.div("ops")
        opButtons = Op.entries.associateWith { op ->
            ops.child<HTMLButtonElement>("button", "op", op.symbol).apply {
                setAttribute("aria-label", texts.opName(op))
                addEventListener("click", { act(state.tapOp(op)) })
            }
        }
        val buttons = controls.div("buttons")
        undoButton = buttons.child<HTMLButtonElement>("button", "outlined", texts.undo).apply {
            addEventListener("click", { act(state.undo()) })
        }
        resetButton = buttons.child<HTMLButtonElement>("button", "outlined", texts.reset).apply {
            addEventListener("click", { act(state.reset()) })
        }
        hintButton = buttons.child<HTMLButtonElement>("button", "outlined", texts.hint).apply {
            title = texts.hintCost
            addEventListener("click", { act(state.requestHint()) })
        }

        stepsBox = game.child("section", "steps")
        stepsBox.span("label", texts.steps)
        stepsList = stepsBox.child("ol")

        val footer = root.child<HTMLElement>("footer", "footer")
        val rules = footer.child<HTMLElement>("details")
        rules.child<HTMLElement>("summary", text = texts.howToPlay)
        texts.howToPlayText.forEach { rules.paragraph(text = it) }
        val promo = footer.paragraph("promo", texts.appPromo + " ")
        if (APP_PUBLISHED) {
            promo.child<HTMLAnchorElement>("a", text = texts.appLink).href = GOOGLE_PLAY_URL
        } else {
            promo.span(text = texts.appComingSoon)
        }
        val note = footer.paragraph("note", texts.storageNote + " · ")
        note.child<HTMLAnchorElement>("a", text = texts.privacyPolicy).href = "privacidad.html"

        // La tarjeta de reto completado sale, como en la app, en una ventana en el centro de la
        // pantalla, sobre el tablero oscurecido.
        overlay = root.div("overlay")
        overlay.setAttribute("role", "dialog")
        overlay.setAttribute("aria-modal", "true")
        overlay.setAttribute("aria-labelledby", "result-title")
        behindOverlay = listOf(header, game, footer)
        solvedCard = overlay.child("section", "result")
        solvedTitle = solvedCard.child("h2", text = texts.dailySolved)
        solvedTitle.id = "result-title"
        // Para llevar ahí el foco al resolver.
        solvedTitle.tabIndex = -1
        solvedStars = solvedCard.div("stars")
        solvedStars.setAttribute("role", "img")
        solvedSteps = solvedCard.paragraph()
        solvedTip = solvedCard.paragraph("tip")
        solvedStreak = solvedCard.paragraph("streak")
        val solvedButtons = solvedCard.div("buttons")
        solvedButtons.child<HTMLButtonElement>("button", "outlined", texts.replay).apply {
            addEventListener("click", {
                act(state.reset())
                tiles.firstOrNull()?.button?.focus()
            })
        }
        shareButton = solvedButtons.child<HTMLButtonElement>("button", "filled", texts.share).apply {
            addEventListener("click", { share() })
        }
        solvedCard.paragraph("tomorrow", texts.comeBackTomorrow)

        viewportProbe = root.div("viewport-probe")
        viewportProbe.setAttribute("aria-hidden", "true")
    }

    fun start() {
        load(day)
        // Al cambiar el tamaño de la ventana o girar el móvil, las fichas se vuelven a ajustar.
        window.addEventListener("resize", { scheduleFit() })
        // La sonda avisa también cuando la pantalla cambia sin evento de tamaño, o cuando los
        // estilos llegan después del primer ajuste.
        val resizeObserver = window.asDynamic().ResizeObserver
        if (resizeObserver != undefined) {
            val observer = js("Reflect").construct(resizeObserver, arrayOf({ _: dynamic -> scheduleFit() }))
            observer.observe(viewportProbe)
        }
        document.addEventListener("keydown", { event -> onKey(event as KeyboardEvent) })
        // Si la página se queda abierta de un día para otro, al volver a ella sale el reto nuevo.
        document.addEventListener("visibilitychange", {
            if (document.asDynamic().visibilityState == "visible" && today() != day) load(today())
        })
    }

    private fun load(newDay: Long) {
        day = newDay
        val level = dailyLevel(day)
        state = store.loadGame(day)?.let { restoreGame(level, it) } ?: GameState(level)
        buildTiles()
        render()
        fitBoard()
    }

    /**
     * Ajusta las fichas en cuanto termina lo que está pasando, una sola vez aunque lleguen varios
     * avisos seguidos. Con un temporizador y no con requestAnimationFrame, que no llega mientras la
     * pestaña está en segundo plano.
     */
    private fun scheduleFit() {
        if (fitPending) return
        fitPending = true
        window.setTimeout({
            fitPending = false
            fitBoard()
        }, 0)
    }

    /**
     * Ajusta el tamaño de las fichas para que el tablero y los controles quepan en
     * la altura visible sin desplazarse. En muchos portátiles y en los móviles
     * girados, la pantalla es más baja que la partida con las fichas a tamaño
     * completo. Las fichas son lo único que cambia de alto, así que basta con
     * medir cuánto sobra o falta y repartirlo entre sus dos filas.
     */
    private fun fitBoard() {
        val tile = tiles.firstOrNull()?.button ?: return
        // Si el tablero va centrado junto al panel (en horizontal), crece la mitad de lo que se le
        // da: unas pocas pasadas llegan al tamaño justo.
        repeat(3) {
            val board = tileBox.getBoundingClientRect()
            val rect = controls.getBoundingClientRect()
            // En vertical los controles van debajo del tablero y también tienen que caber. Si van
            // en otra columna, no cuentan aunque queden más abajo que un tablero pequeño. Al
            // resolver se ocultan, pero conservan su sitio, así que se pueden medir igual.
            val sameColumn = rect.left < board.right && rect.right > board.left
            val controlsBelow = if (sameColumn && rect.top >= board.bottom) rect.bottom - board.bottom else 0.0
            val bottom = board.bottom + window.scrollY + controlsBelow + FIT_MARGIN_PX
            val overflow = bottom - viewportProbe.offsetHeight
            val fit = maxOf(MIN_TILE_PX, tile.offsetHeight - overflow / 2)
            if (abs(fit - tileFit) < 0.5) return
            tileFit = fit
            (document.documentElement as HTMLElement).style.setProperty("--tile-fit", "${fit}px")
        }
    }

    /** Pasa al estado [next] tras una acción del jugador, lo guarda y lo muestra. */
    private fun act(next: GameState) {
        val before = state
        if (next == before) return
        state = next
        if (next.solved && !before.solved) {
            progress = progress.complete(day, next.stars)
            store.saveProgress(progress)
        }
        store.saveGame(day, next.encode().takeIf { next.worthSaving })
        render()
        if (next.steps.size > before.steps.size) next.lastFuse?.let { pop(tiles[it.to].button) }
        if (next.solved && !before.solved) {
            replay(solvedStars, "animated")
            solvedTitle.focus()
            confettiBurst()
        }
    }

    private fun onKey(event: KeyboardEvent) {
        // Con la tarjeta de reto completado a la vista, el teclado no toca la partida.
        if (state.solved || event.ctrlKey || event.metaKey || event.altKey) return
        val next = when (event.key) {
            "+" -> state.tapOp(Op.ADD)
            "-" -> state.tapOp(Op.SUB)
            "*", "x", "X" -> state.tapOp(Op.MUL)
            "/" -> state.tapOp(Op.DIV)
            "Backspace" -> state.undo()
            "Escape" -> state.selected?.let { state.tapTile(it) } ?: return
            else -> return
        }
        event.preventDefault()
        act(next)
    }

    private fun buildTiles() {
        tileBox.innerHTML = ""
        tileBox.style.setProperty("--per-row", if (state.tiles.size <= 4) "2" else "3")
        tiles = state.tiles.indices.map { index ->
            TileView(tileBox.child("button", "tile")).also { tile ->
                tile.button.addEventListener("click", { act(state.tapTile(index)) })
            }
        }
    }

    private fun render() {
        val state = state
        val streak = progress.currentStreak(day)
        challengeName.textContent = texts.numberedChallenge(dailyNumber(day))
        challengeDate.textContent = formatDay(day, texts.locale)
        statusLine.textContent = when {
            progress.isCompleted(day) -> texts.doneToday(streak)
            streak > 0 -> texts.streak(streak)
            else -> texts.dailyTagline
        }
        targetValue.textContent = state.target.toString()
        renderExpression(state)
        renderTiles(state)

        // Hace falta una ficha elegida y al menos otra con la que operarla.
        val usable = state.selected != null && state.tiles.count { it != null } > 1
        opButtons.forEach { (op, button) ->
            button.disabled = !usable
            button.classList.toggle("selected", op == state.op)
            button.setAttribute("aria-pressed", (op == state.op).toString())
        }
        undoButton.disabled = state.steps.isEmpty()
        resetButton.disabled = state.steps.isEmpty()
        hintButton.disabled = state.hint != null
        // Como en la app, al resolver los controles dejan de verse pero conservan su sitio: así no se
        // mueve nada detrás de la tarjeta.
        controls.classList.toggle("concealed", state.solved)

        overlay.hidden = !state.solved
        // Mientras se ve la tarjeta, lo de detrás no responde ni se recorre con el teclado, y la
        // página no se desplaza.
        behindOverlay.forEach { if (state.solved) it.setAttribute("inert", "") else it.removeAttribute("inert") }
        document.body?.classList?.toggle("modal-open", state.solved)
        if (state.solved) {
            solvedStars.setAttribute("aria-label", texts.starsDescription(state.stars, MAX_STARS))
            solvedStars.innerHTML = ""
            repeat(MAX_STARS) { solvedStars.span(if (it < state.stars) "star lit" else "star", "★") }
            solvedSteps.textContent = texts.solvedInSteps(state.steps.size)
            solvedTip.textContent = texts.threeStarsTip(state.level.minSteps)
            solvedTip.hidden = state.stars == MAX_STARS
            solvedStreak.textContent = texts.streak(streak)
        }

        stepsBox.hidden = state.steps.isEmpty()
        stepsList.innerHTML = ""
        state.steps.forEach { stepsList.child<HTMLElement>("li", text = it.text) }
    }

    private fun renderExpression(state: GameState) {
        val first = state.selected?.let { state.tiles[it] }
        val op = state.op
        expression.textContent = when {
            state.solved -> state.steps.last().text
            first == null -> ""
            op == null -> "$first"
            else -> "$first ${op.symbol} ?"
        }
        val hint = state.hint
        guidance.textContent = when {
            hint is Hint.NextStep -> texts.hintNextStep(hint.step.operation)
            hint is Hint.DeadEnd -> texts.hintDeadEnd
            state.solved -> texts.solvedHint
            state.tiles.count { it != null } == 1 -> texts.stuck
            first == null -> texts.pickNumber
            op == null -> texts.pickOperation
            else -> texts.pickSecondNumber
        }
        guidance.className = when {
            state.solved -> "guidance solved"
            hint != null -> "guidance hinted"
            else -> "guidance"
        }
    }

    private fun renderTiles(state: GameState) {
        val hinted = state.hintedTiles
        tiles.forEachIndexed { index, tile ->
            val value = state.tiles[index]
            val button = tile.button
            if (value == null) {
                // Hueco que ha dejado una ficha operada: solo ocupa su sitio.
                button.className = "tile empty"
                button.disabled = true
                button.setAttribute("aria-hidden", "true")
                tile.value.textContent = ""
                tile.preview.textContent = ""
                return@forEachIndexed
            }
            val selected = index == state.selected
            val preview = state.preview(index)
            button.removeAttribute("aria-hidden")
            button.disabled = state.solved
            button.setAttribute("aria-pressed", selected.toString())
            button.classList.remove("empty")
            button.classList.toggle("selected", selected)
            button.classList.toggle("goal", value == state.target)
            // Con número y operación elegidos, las fichas con las que no se puede operar se atenúan.
            button.classList.toggle("blocked", state.op != null && !selected && preview == null)
            button.classList.toggle("hinted", index in hinted)
            tile.value.textContent = value.toString()
            tile.value.className = when {
                value < 1_000 -> "value"
                value < 100_000 -> "value medium"
                else -> "value small"
            }
            tile.preview.textContent = preview?.let { "= $it" } ?: ""
            tile.preview.classList.toggle("goal", preview == state.target)
        }
    }

    /** La ficha que recibe el resultado de una operación rebota un momento. */
    private fun pop(element: HTMLElement) = replay(element, "pop")

    /** Vuelve a lanzar la animación CSS de [className] en [element]. */
    private fun replay(element: HTMLElement, className: String) {
        element.classList.remove(className)
        // Leer el tamaño obliga al navegador a aplicar el cambio antes de volver a poner la clase.
        element.offsetWidth
        element.classList.add(className)
    }

    /**
     * Comparte solo el enlace a la web, sin texto: con el menú del sistema en el
     * móvil y copiándolo al portapapeles en el ordenador.
     */
    private fun share() {
        val url = URL(SHARED_PAGE, window.location.href).href
        val navigator = window.navigator.asDynamic()
        val touch = window.matchMedia("(pointer: coarse)").matches
        if (touch && navigator.share != undefined) {
            (navigator.share(json("url" to url)) as Promise<Any?>).then(
                onFulfilled = {},
                onRejected = { error -> if (error.asDynamic().name != "AbortError") copy(url) },
            )
        } else {
            copy(url)
        }
    }

    private fun copy(text: String) {
        val clipboard = window.navigator.asDynamic().clipboard
        if (clipboard == undefined) {
            copyWithSelection(text)
            return
        }
        (clipboard.writeText(text) as Promise<Any?>).then(
            onFulfilled = { showCopied() },
            onRejected = { copyWithSelection(text) },
        )
    }

    /** La forma antigua de copiar, para los navegadores que no dejan usar el portapapeles. */
    private fun copyWithSelection(text: String) {
        val area = document.body?.child<HTMLTextAreaElement>("textarea") ?: return
        area.value = text
        area.setAttribute("readonly", "")
        area.style.position = "fixed"
        area.style.opacity = "0"
        area.select()
        val copied = try {
            document.asDynamic().execCommand("copy") as Boolean
        } catch (e: Throwable) {
            false
        }
        area.remove()
        if (copied) showCopied()
    }

    private fun showCopied() {
        shareButton.textContent = texts.copied
        window.setTimeout({ shareButton.textContent = texts.share }, COPIED_MILLIS)
    }
}
